import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { Trash } from "lucide-react";
import { toast, ToastContainer } from "react-toastify"; // Import toast for notifications
import { BsBoxArrowInDownLeft } from "react-icons/bs";
import { HiOutlineTrash } from "react-icons/hi";
import { FaPlus } from "react-icons/fa";
import Select from "react-select";

import "react-toastify/dist/ReactToastify.css";
import ImageModal from "../Items Master/ImageModal";
export default function MaterialInwardRegister() {
  const { grnId: grnIdFromParams } = useParams();
  const [items, setItems] = useState([]);
  const [grnId, setGrnId] = useState(grnIdFromParams);
  const [supplierName, setSupplierName] = useState("");
  const [timestamp, setTimestamp] = useState("");
  const [containerSeal, setContainerSeal] = useState("");
  const [invoiceNo, setInvoiceNo] = useState("");
  const [invoiceCopy, setInvoiceCopy] = useState("");
  const [warehouseName, setWarehouseName] = useState("");
  const [inlandCharges, setInlandCharges] = useState(0);
  const [agentComm, setAgentComm] = useState(0);
  const [freight, setFreight] = useState(0);
  const [customDuty, setCustomDuty] = useState(0);
  const [clearingAgentFee, setClearingAgentFee] = useState(0);
  const [transport, setTransport] = useState(0);
  const [unloadingCharges, setUnloadingCharges] = useState(0);
  const [Cfs, setCfs] = useState(0);
  const [DO, setDO] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [warehouseNames, setWarehouseNames] = useState([]);
  const [itemsList, setItemsList] = useState([]);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");

  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
    setIsImageModalOpen(true);
  };

  const closeImageModal = () => {
    setIsImageModalOpen(false);
    setSelectedImage("");
  };

  // Fetch items on component mount
  useEffect(() => {
    const fetchItems = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(
          "https://category-management-product-backend.onrender.com/api/categories",
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (response.status === 200) {
          setItemsList(response.data);
        }
      } catch (err) {
        console.error("Error fetching items:", err);
        setError("Failed to fetch items.");
      }
    };
    fetchItems();
  }, []);
  // Update grnId when URL changes
  useEffect(() => {
    setGrnId(grnIdFromParams);
  }, [grnIdFromParams]);

  // Fetch details when grnId changes
  useEffect(() => {
    if (grnId) {
      fetchDetails(grnId);
    }
  }, [grnId]);

  // Fetch warehouse names on component mount
  useEffect(() => {
    const fetchWarehouseNames = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(
          "https://category-management-product-backend.onrender.com/api/warehouse/get-warehouse/name",
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (response.status === 200) {
          const warehouseList = response.data.data;
          const names = warehouseList.map(
            (warehouse) => warehouse.warehouseName
          );
          setWarehouseNames(names);
        }
      } catch (err) {
        console.error("Error fetching warehouse names:", err);
        setError("Failed to fetch warehouse names.");
      }
    };
    fetchWarehouseNames();
  }, []);

  // Update status
  const updateStatus = async (newStatus) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(
        `https://category-management-product-backend.onrender.com/api/purchase-order/purchase-order/status/${grnId}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.status === 200) {
        setStatus(newStatus);
      }
    } catch (error) {
      console.error("Error updating status:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle file upload
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      setLoading(true);
      setError("");
      const response = await fetch(
        "https://category-management-product-backend.onrender.com/api/upload/upload",
        {
          method: "POST",
          body: formData,
        }
      );
      if (!response.ok)
        throw new Error("File upload failed. Please try again.");
      const data = await response.json();
      if (data.fileUrls && data.fileUrls.length > 0) {
        setInvoiceCopy(data.fileUrls[0]);
      } else {
        throw new Error("No URL received from the server.");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch details
  const fetchDetails = async (referenceNo) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `https://category-management-product-backend.onrender.com/api/purchase-order/purchase-order/${referenceNo}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = response.data.data;

      // Calculate totalQty for each item
      const itemsWithTotalQty = data.items.map((item) => ({
        itemCategory: item.Category,
        itemCode: item.itemCode,
        itemName: item.itemDescription,
        orderContainer: item.orderContainer || 0,
        ctns: item.ctns || item.orderContainer || 0,
        qtyPerCtn: item.Qtyctn || 0,
        totalQty: (item.Qtyctn || 0) * (item.ctns || item.orderContainer || 0), // Calculate totalQty
        PricePerUnit: item.PricePerUnit,
        totalItemAmount: item.totalItemAmount || 0,
        weightPerCtn: item.Wtctn || 0,
        totalWeight: item.TotalWeight || 0,
        cbmPerCtn: item.Cbmctn || 0,
        totalCBM: item.TotalCbm || 0,
        shippingMark: item.ShippingMark || "",
        rmb: 0,
        inr: 0,
        image: item.itemImage,
        isNew: false, // Mark existing rows as not new
      }));

      setGrnId(data.referenceNo);
      setTimestamp(new Date(data.createdAt).toLocaleString());
      setSupplierName(data.toPerson);
      setContainerSeal(data.containerSeal);
      setInvoiceNo(data.invoiceNo);
      setItems(itemsWithTotalQty); // Set items with calculated totalQty
      setInlandCharges(data.inlandCharges);
      setAgentComm(data.agentComm);
      setFreight(data.freight);
      setCustomDuty(data.customDuty);
      setClearingAgentFee(data.clearingAgentFee);
      setTransport(data.transport);
      setUnloadingCharges(data.unloadingCharges);
      setTotalAmount(data.totalAmount);
      setWarehouseName(data.warehouseName);
      setInvoiceCopy(data.invoiceCopy);
      setCfs(data.Cfs);
      setDO(data.DO);
      setStatus(data.status);
    } catch (error) {
      console.error("Error fetching details:", error);
      alert("Error fetching data. Please check the reference number.");
    }
  };

  // Calculate total amount
  const calculateTotalAmount = () => {
    const total =
      (inlandCharges || 0) +
      (agentComm || 0) +
      (freight || 0) +
      (customDuty || 0) +
      (clearingAgentFee || 0) +
      (transport || 0) +
      (unloadingCharges || 0) +
      (Cfs || 0) +
      (DO || 0);
    setTotalAmount(total);
  };

  // Handle input changes
  const handleInputChange = (e, setterFunction) => {
    const value = Number(e.target.value);
    setterFunction(value);
    calculateTotalAmount();
  };

  // Handle item changes
  const handleItemChange = (index, field, value) => {
    const updatedItems = [...items];
    updatedItems[index] = { ...updatedItems[index], [field]: value };

    if (field === "qtyPerCtn" || field === "ctns") {
      updatedItems[index].totalQty =
        updatedItems[index].qtyPerCtn * updatedItems[index].ctns;
    }
    if (field === "PricePerUnit" || field === "totalQty") {
      updatedItems[index].totalItemAmount =
        updatedItems[index].PricePerUnit * updatedItems[index].totalQty;
    }
    if (field === "weightPerCtn" || field === "ctns") {
      updatedItems[index].totalWeight =
        updatedItems[index].weightPerCtn * updatedItems[index].ctns;
    }
    if (field === "cbmPerCtn" || field === "ctns") {
      updatedItems[index].totalCBM =
        updatedItems[index].cbmPerCtn * updatedItems[index].ctns;
    }
    if (updatedItems[index].totalCBM > 0) {
      const perCbmPrice = totalAmount / updatedItems[index].totalCBM;
      const perUnitPrice = perCbmPrice / updatedItems[index].totalQty;
      updatedItems[index].PricePerUnit = perUnitPrice;
    }
    setItems(updatedItems);
    calculateTotalAmount();
  };

  // Update purchase order
  const updatePurchaseOrder = async () => {
    const token = localStorage.getItem("token");
    const updatedData = {
      referenceNo: grnId,
      containerSeal,
      invoiceNo,
      invoiceCopy,
      inlandCharges,
      agentComm,
      freight,
      customDuty,
      clearingAgentFee,
      transport,
      unloadingCharges,
      warehouseName,
      Cfs,
      DO,
      items: items.map((item) => ({
        itemCode: item.itemCode,
        itemDescription: item.itemName,
        Category: item.itemCategory,
        unit: "Box",
        itemImage: item.image,
        orderContainer: item.orderContainer,
        ctns: item.ctns,
        Qtyctn: item.qtyPerCtn,
        Wtctn: item.weightPerCtn,
        Cbmctn: item.cbmPerCtn,
        ShippingMark: item.shippingMark,
        PricePerUnit: item.PricePerUnit,
        totalItemAmount: item.totalItemAmount,
      })),
    };

    try {
      const response = await axios.put(
        `https://category-management-product-backend.onrender.com/api/purchase-order/update/${grnId}`,
        updatedData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.status === 200) {
        toast.success("Material Inward Successfully");
      }
    } catch (error) {
      console.error("Error updating data:", error);
      toast.error("Error updating data.");
    }
  };

  // Add new row
  const addNewRow = () => {
    const newItem = {
      itemCategory: "",
      itemCode: "",
      itemName: "",
      orderContainer: 0,
      ctns: 0,
      qtyPerCtn: 0,
      totalQty: 0,
      PricePerUnit: 0,
      totalItemAmount: 0,
      weightPerCtn: 0,
      totalWeight: 0,
      cbmPerCtn: 0,
      totalCBM: 0,
      shippingMark: "",
      image: "",
      isNew: true, // Mark new rows as editable
    };
    setItems([...items, newItem]);
  };
  // Function to delete a row
  const deleteRow = (index) => {
    const updatedItems = items.filter((_, i) => i !== index); // Remove the row at the specified index
    setItems(updatedItems);
  };

  const handleItemNameChange = (index, selectedItemName) => {
    const selectedItem = itemsList.find(
      (item) => item.itemName === selectedItemName
    );
    if (selectedItem) {
      const updatedItems = [...items];
      updatedItems[index] = {
        ...updatedItems[index],
        itemCategory: selectedItem.categoryName,
        itemCode: selectedItem.itemCode,
        itemName: selectedItem.itemName,
        image: selectedItem.itemImage,
        qtyPerCtn: selectedItem.pcsPerCtn,
        cbmPerCtn: selectedItem.cbmPerCtn,
        weightPerCtn: selectedItem.wtPerCtn,
      };
      setItems(updatedItems);
    }
  };

  return (
    <div className="">
      {/* Header */}
      <div className=" text-gray-700 w-fit mb-2 text-lg flex items-center gap-2 capitalize font-semibold ">
        <BsBoxArrowInDownLeft />
        Material Inward Register
      </div>

      {/* Top Section */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 mb-0.5 p-0.5 border border-gray-200 rounded-lg bg-gray-50">
        {/* First Column */}
        <div className="space-y-0.5">
          <div className="grid grid-cols-2 gap-1 items-center">
            <div className="bg-gray-100 font-medium text-gray-600 text-xs p-1 rounded">
              GRN ID
            </div>
            <div className="flex gap-1">
              <input
                readOnly
                value={grnId}
                className="border border-gray-300 rounded px-1.5 py-1 w-full text-xs bg-gray-100 focus:ring-1 focus:ring-blue-500"
                placeholder="Enter Reference No."
              />
            </div>
          </div>

          {[
            { label: "TimeStamp", value: timestamp, readOnly: true },
            { label: "Supplier Name", value: supplierName, readOnly: true },
            {
              label: "Container & Seal",
              value: containerSeal,
              setter: setContainerSeal,
            },
            { label: "Invoice No.", value: invoiceNo, setter: setInvoiceNo },
          ].map((field, idx) => (
            <div key={idx} className="grid grid-cols-2 gap-1 items-center">
              <div className="bg-gray-100 font-medium text-gray-600 text-xs p-1 rounded">
                {field.label}
              </div>
              <input
                value={field.value}
                onChange={field.setter && ((e) => field.setter(e.target.value))}
                className={`border border-gray-300 rounded px-1.5 py-1 w-full text-xs ${
                  field.readOnly ? "bg-gray-100" : "bg-white"
                }`}
                readOnly={field.readOnly}
              />
            </div>
          ))}

          <div className="grid grid-cols-2 gap-1 items-center">
            <div className="bg-gray-100 font-medium text-gray-600 text-xs p-1 rounded">
              Invoice Copy
            </div>
            <div>
              <input
                type="file"
                onChange={handleFileUpload}
                className="border border-gray-300 rounded px-1 py-1 w-full text-xs bg-white file:mr-2 file:py-0.5 file:px-2 file:rounded file:border-0 file:text-xs file:font-medium file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"
              />
            </div>
          </div>

          {loading && (
            <div className="text-xs text-blue-600 animate-pulse">
              Uploading...
            </div>
          )}
          {error && <div className="text-xs text-red-600">{error}</div>}

          <div className="grid grid-cols-2 gap-1 items-center">
            <div className="bg-gray-100 font-medium text-gray-600 text-xs p-1 rounded">
              Warehouse Name
            </div>
            <select
              value={warehouseName}
              onChange={(e) => setWarehouseName(e.target.value)}
              className="border border-gray-300 rounded px-1.5 py-1 w-full text-xs bg-blue-50 focus:ring-1 focus:ring-blue-500"
            >
              <option value="" disabled>
                Select Warehouse
              </option>
              {warehouseNames.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Second Column - Charges Inputs */}
        <div className="space-y-0.5">
          {[
            {
              label: "Inland Charges (INR)",
              state: inlandCharges,
              setter: setInlandCharges,
            },
            {
              label: "Agent Comm. (2% ON CIF)",
              state: agentComm,
              setter: setAgentComm,
            },
            { label: "Freight (INR)", state: freight, setter: setFreight },
            {
              label: "Custom Duty (INR)",
              state: customDuty,
              setter: setCustomDuty,
            },
            {
              label: "Clearing Agent Fee (INR)",
              state: clearingAgentFee,
              setter: setClearingAgentFee,
            },
            { label: "CFS (INR)", state: Cfs, setter: setCfs },
            { label: "DO (INR)", state: DO, setter: setDO },
          ].map((field, idx) => (
            <div key={idx} className="grid grid-cols-2 gap-1 items-center">
              <div className="bg-gray-100 font-medium text-gray-600 text-xs p-1 rounded">
                {field.label}
              </div>
              <input
                type="number"
                value={field.state}
                onChange={(e) => handleInputChange(e, field.setter)}
                className="border border-gray-300 rounded px-1.5 py-1 w-full text-xs bg-white focus:ring-1 focus:ring-blue-500"
              />
            </div>
          ))}
        </div>

        {/* Third Column - Status and Actions */}
        <div className="space-y-0.5">
          {[
            {
              label: "Transport (INR)",
              state: transport,
              setter: setTransport,
            },
            {
              label: "Unloading Chrgs. (INR)",
              state: unloadingCharges,
              setter: setUnloadingCharges,
            },
          ].map((field, idx) => (
            <div key={idx} className="grid grid-cols-2 gap-1 items-center">
              <div className="bg-gray-100 font-medium text-gray-600 text-xs p-1 rounded">
                {field.label}
              </div>
              <input
                type="number"
                value={field.state}
                onChange={(e) => handleInputChange(e, field.setter)}
                className="border border-gray-300 rounded px-1.5 py-1 w-full text-xs bg-white focus:ring-1 focus:ring-blue-500"
              />
            </div>
          ))}

          <div className="grid grid-cols-2 gap-1 items-center">
            <div className="bg-gray-100 font-medium text-gray-600  text-xs p-1 rounded">
              Status
            </div>
            <select
              value={status}
              onChange={(e) => updateStatus(e.target.value)}
              className="border border-gray-300 rounded px-1.5 py-1 w-full text-xs bg-gray-50 focus:ring-1 focus:ring-blue-500"
              disabled={loading}
            >
              <option value="open">Open</option>
              <option value="closed">Closed</option>
            </select>
          </div>

          <div className="grid grid-cols-2 gap-1 items-center">
            <div className="bg-blue-200 font-bold text-xs p-1 rounded">
              Total Amount
            </div>
            <input
              type="number"
              value={totalAmount.toFixed(2)}
              className="border border-gray-300 rounded px-1.5 py-1 w-full text-xs bg-green-100 font-medium"
              readOnly
            />
          </div>

          <div className="flex justify-end pt-2">
            <button
              className="bg-green-500 hover:bg-green-600 text-white font-bold py-1 px-3 rounded-md text-xs shadow-sm transition-colors duration-200"
              onClick={updatePurchaseOrder}
              disabled={loading}
            >
              {loading ? (
                <span className="animate-pulse">Updating...</span>
              ) : (
                "OK"
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Add Row Button */}
      <div className="flex justify-end my-2">
        <button
          onClick={addNewRow}
          className="bg-blue-500 flex items-center gap-1 hover:bg-blue-600 text-white font-medium py-2 px-3 rounded-md text-xs shadow-sm transition-colors duration-200"
        >
          <FaPlus size={12} /> Add Row
        </button>
      </div>

      <div
        className="overflow-x-auto max-w-[100vw] rounded-md max-h-[40rem] border border-gray-300"
        style={{ scrollbarWidth: "thin" }}
      >
        <table className="min-w-full table-auto rounded-lg shadow-md border-spacing-0 border-collapse">
          <thead className="top-0 z-50 sticky text-left bg-gray-700 text-white text-[11px]">
            <tr>
              <th className="px-2 py-1 font-normal tracking-wide">
                Item Name *
              </th>
              <th className="px-2 py-1 font-normal tracking-wide">Category</th>
              <th className="px-2 py-1 font-normal tracking-wide">Code</th>
              <th
                className="px-2 py-1 w-[5%] font-normal tracking-wide"
                title=" Ordered Qty/Ctn *"
              >
                O.Qty/Ctn*
              </th>
              <th
                className="px-2 py-1 w-[5%] font-normal tracking-wide"
                title="Received Ctns"
              >
                Rc. Ctns
              </th>
              <th className="px-2 py-1 w-[5%] font-normal tracking-wide">
                Qty/Ctn
              </th>
              <th className="px-2 py-1 w-[5%] font-normal tracking-wide">
                Total Qty
              </th>
              <th className="px-2 py-1 w-[5%] font-normal tracking-wide">
                Price/Unit
              </th>
              <th className="px-2 py-1 w-[5%] font-normal tracking-wide">
                Wt/Ctn
              </th>
              <th
                className="px-2 py-1 w-[5%] font-normal tracking-wide"
                title="Total Weight"
              >
                T. Weight
              </th>
              <th className="px-2 py-1 w-[5%] font-normal tracking-wide">
                CBM/Ctn
              </th>
              <th
                className="px-2 py-1 w-[5%] font-normal tracking-wide"
                title="Total CBM"
              >
                T. CBM
              </th>
              <th
                className="px-2 py-1 font-normal tracking-wide"
                title="Shipping Mark"
              >
                Ship. Mark
              </th>
              <th
                className="px-2 py-1 w-[7%] font-normal tracking-wide"
                title="Total Amount"
              >
                T. Amount
              </th>
              <th className="px-2 py-1 font-normal tracking-wide">Img</th>
              <th className="px-2 py-1 font-normal tracking-wide"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => (
              <tr
                key={index}
                className="hover:bg-gray-50 border-b divide-x transition-colors duration-200"
              >
                <td className="">
                  {item.isNew ? (
                    <Select
                      options={itemsList.map((item) => ({
                        value: item.itemName,
                        label: item.itemName,
                      }))}
                      value={
                        itemsList.find(
                          (opt) => opt.itemName === item.itemName
                        ) || null
                      }
                      onChange={(option) =>
                        handleItemNameChange(index, option ? option.value : "")
                      }
                      placeholder="Select Item"
                      isClearable
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        menu: (provided) => ({
                          ...provided,
                          width: "30rem", // Set your desired width here
                        }),
                        control: (provided) => ({
                          ...provided,
                          minHeight: "28px",
                          height: "28px",
                          border: "none",
                          boxShadow: "none",
                          "&:hover": { border: "none" },
                        }),
                        valueContainer: (provided) => ({
                          ...provided,
                          height: "28px",
                          padding: "0 4px",
                        }),
                        input: (provided) => ({
                          ...provided,
                          margin: 0,
                        }),
                        indicatorsContainer: (provided) => ({
                          ...provided,
                          height: "28px",
                        }),
                      }}
                      className="w-full text-[11px] text-gray-700 bg-cyan-100"
                    />
                  ) : (
                    <input
                      value={item.itemName}
                      readOnly
                      className="w-full px-2 py-1 text-[11px] text-gray-500 bg-gray-100 outline-none"
                    />
                  )}
                </td>
                <td className="">
                  <input
                    value={item.itemCategory}
                    readOnly
                    className="w-full px-2 py-1 text-[11px] text-gray-500 bg-gray-100 outline-none"
                  />
                </td>
                <td className="">
                  <input
                    value={item.itemCode}
                    readOnly
                    className="w-full px-2 py-1 text-[11px] text-gray-500 bg-gray-100 outline-none"
                  />
                </td>
                <td className="">
                  <input
                    type="number"
                    value={item.orderContainer}
                    onChange={(e) =>
                      handleItemChange(index, "orderContainer", e.target.value)
                    }
                    className={`w-full px-2 py-1 text-[11px] text-gray-700 outline-none ${
                      item.isNew ? "bg-cyan-100" : "bg-gray-100"
                    }`}
                    readOnly={!item.isNew}
                  />
                </td>
                <td className="">
                  <input
                    type="number"
                    value={item.ctns}
                    onChange={(e) =>
                      handleItemChange(index, "ctns", e.target.value)
                    }
                    className="w-full px-2 py-1 text-[11px] text-gray-700 bg-cyan-100 outline-none"
                  />
                </td>
                <td className="">
                  <input
                    type="number"
                    value={item.qtyPerCtn}
                    onChange={(e) =>
                      handleItemChange(
                        index,
                        "qtyPerCtn",
                        Number(e.target.value)
                      )
                    }
                    className={`w-full px-2 py-1 text-[11px] text-gray-500 outline-none ${
                      item.isNew ? "bg-cyan-100" : "bg-gray-100"
                    }`}
                    readOnly={!item.isNew}
                  />
                </td>
                <td className="">
                  <input
                    type="number"
                    value={item.totalQty}
                    readOnly
                    className="w-full px-2 py-1 text-[11px] text-gray-500 bg-gray-100 outline-none"
                  />
                </td>
                <td className="">
                  <input
                    type="text"
                    value={item.PricePerUnit.toFixed(2)}
                    onChange={(e) =>
                      handleItemChange(
                        index,
                        "PricePerUnit",
                        Number(e.target.value)
                      )
                    }
                    className={`w-full px-2 py-1 text-[11px] text-gray-500 outline-none ${
                      item.isNew ? "bg-cyan-100" : "bg-gray-100"
                    }`}
                    readOnly={!item.isNew}
                  />
                </td>
                <td className="">
                  <input
                    type="number"
                    value={item.weightPerCtn}
                    onChange={(e) =>
                      handleItemChange(
                        index,
                        "weightPerCtn",
                        Number(e.target.value)
                      )
                    }
                    className={`w-full px-2 py-1 text-[11px] text-gray-500 outline-none ${
                      item.isNew ? "bg-cyan-100" : "bg-gray-100"
                    }`}
                    readOnly={!item.isNew}
                  />
                </td>
                <td className="">
                  <input
                    type="number"
                    value={item.totalWeight}
                    readOnly
                    className="w-full px-2 py-1 text-[11px] text-gray-500 bg-gray-100 outline-none"
                  />
                </td>
                <td className="">
                  <input
                    type="number"
                    value={item.cbmPerCtn}
                    onChange={(e) =>
                      handleItemChange(
                        index,
                        "cbmPerCtn",
                        Number(e.target.value)
                      )
                    }
                    className={`w-full px-2 py-1 text-[11px] text-gray-500 outline-none ${
                      item.isNew ? "bg-cyan-100" : "bg-gray-100"
                    }`}
                    readOnly={!item.isNew}
                  />
                </td>
                <td className="">
                  <input
                    type="number"
                    value={item.totalCBM}
                    readOnly
                    className="w-full px-2 py-1 text-[11px] text-gray-500 bg-gray-100 outline-none"
                  />
                </td>
                <td className="">
                  <input
                    value={item.shippingMark}
                    onChange={(e) =>
                      handleItemChange(index, "shippingMark", e.target.value)
                    }
                    className="w-full px-2 py-1 text-[11px] text-gray-700 bg-cyan-100 outline-none"
                  />
                </td>
                <td className="">
                  <input
                    type="text"
                    value={item.totalItemAmount.toFixed(2)}
                    readOnly
                    className="w-full px-2 py-1 text-[11px] text-gray-500 bg-gray-100 outline-none"
                  />
                </td>
                <td className=" text-center">
                  <img
                    src={item.image}
                    alt={item.itemName}
                    className="size-4 rounded-md cursor-pointer transition-transform hover:scale-105 object-contain"
                    onClick={() => handleImageClick(item.image)}
                  />
                </td>
                <td className="px-1 text-center">
                  <button
                    type="button"
                    onClick={() => deleteRow(index)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <HiOutlineTrash size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {isImageModalOpen && (
        <ImageModal imageUrl={selectedImage} onClose={closeImageModal} />
      )}
      <ToastContainer />
    </div>
  );
}
